import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Shared table chrome (applies to every table using this primitive):
 * - bordered rounded card
 * - Fixed column layout (sort/page won't reflow widths)
 * - Horizontal + vertical scroll (max-height) with sticky header
 * - Hidden scrollbars + edge fades when content overflows horizontally
 * - Body/header text uses --table-foreground; headers are bold
 */
type TableProps = React.HTMLAttributes<HTMLTableElement> & {
  /** Override the outer chrome wrapper (default: rounded-xl bordered card). */
  wrapperClassName?: string;
  /**
   * Override the scroll region — mainly to drop the default viewport-relative
   * max-height when the page owns its own height (e.g. `max-h-none flex-1`).
   */
  scrollAreaClassName?: string;
};

/**
 * Scroll container with optional left/right fades when content overflows.
 * Scrollbars are hidden globally; fades tell users they can scroll horizontally.
 * Vertical overflow + sticky thead keep column headers visible while scrolling rows.
 */
function TableScrollArea({
  children,
  className,
}: Readonly<{ children: React.ReactNode; className?: string }>) {
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const edgeStateRef = React.useRef({ left: false, right: false });
  const rafRef = React.useRef<number | null>(null);

  const updateEdges = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    const nextLeft = scrollLeft > 1;
    const nextRight = maxScroll > 1 && scrollLeft < maxScroll - 1;

    if (nextLeft !== edgeStateRef.current.left) {
      edgeStateRef.current.left = nextLeft;
      setCanScrollLeft(nextLeft);
    }
    if (nextRight !== edgeStateRef.current.right) {
      edgeStateRef.current.right = nextRight;
      setCanScrollRight(nextRight);
    }
  }, []);

  const scheduleUpdateEdges = React.useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      updateEdges();
    });
  }, [updateEdges]);

  React.useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    updateEdges();
    el.addEventListener("scroll", scheduleUpdateEdges, { passive: true });

    const ro = new ResizeObserver(scheduleUpdateEdges);
    ro.observe(el);
    const table = el.querySelector("table");
    if (table) ro.observe(table);

    return () => {
      el.removeEventListener("scroll", scheduleUpdateEdges);
      ro.disconnect();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [scheduleUpdateEdges, updateEdges]);

  return (
    /**
     * The flex props are inert unless a caller makes the wrapper a flex column —
     * the wrapper is a block by default, so this is a no-op for every table that
     * doesn't opt in, and the escape hatch for the one that does.
     */
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollerRef}
        className={cn(
          "scrollbar-hide max-h-[min(70vh,calc(100dvh-14rem))] w-full overflow-auto overscroll-contain",
          className,
        )}
      >
        {children}
      </div>

      {canScrollLeft ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 z-20 w-10 bg-gradient-to-r from-card via-card/80 to-transparent"
        />
      ) : null}

      {canScrollRight ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 z-20 w-10 bg-gradient-to-l from-card via-card/80 to-transparent"
        />
      ) : null}
    </div>
  );
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, wrapperClassName, scrollAreaClassName, ...props }, ref) => (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-xl border border-border bg-card",
        wrapperClassName,
      )}
    >
      <TableScrollArea className={scrollAreaClassName}>
        <table
          ref={ref}
          className={cn(
            /** Fixed layout: column widths come from header/col rules, not cell content — sort won't reflow. */
            "table-fixed w-max min-w-full caption-bottom text-sm",
            className,
          )}
          {...props}
        />
      </TableScrollArea>
    </div>
  ),
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead
      ref={ref}
      className={cn(
        "sticky top-0 z-sticky bg-table-header shadow-[0_1px_0_0_hsl(var(--border))] [&_tr]:border-b",
        className,
      )}
      {...props}
    />
  ),
);
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
  ),
);
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tfoot ref={ref} className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)} {...props} />
  ),
);
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b border-border transition-colors hover:bg-table-hover data-[state=selected]:bg-table-selected",
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "sticky top-0 z-sticky h-14 w-[10rem] min-w-[10rem] bg-table-header px-4 py-4 text-left align-middle text-sm font-bold leading-5 tracking-normal text-table-foreground normal-case whitespace-nowrap [&:has([role=checkbox])]:w-[2.5rem] [&:has([role=checkbox])]:min-w-[2.5rem] [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        "w-[10rem] min-w-[10rem] px-4 py-5 align-middle text-sm text-table-foreground [&:has([role=checkbox])]:w-[2.5rem] [&:has([role=checkbox])]:min-w-[2.5rem] [&:has([role=checkbox])]:pr-0 [&_.text-muted-foreground]:text-table-foreground/65 [&_.text-foreground]:text-table-foreground",
        className,
      )}
      {...props}
    />
  ),
);
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />
  ),
);
TableCaption.displayName = "TableCaption";

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
